import { handleFilterParams } from './filterParamUtils';
import { createApi } from '@reduxjs/toolkit/query/react';
import BaseQuery from './base.api';
import type {
	FilterField,
	FilterMetadata,
	FilterSearchRequestParams,
} from './models/filters.model';

export const filtersApi = createApi({
	reducerPath: 'filtersApi',
	baseQuery: BaseQuery('/v1/filters'),
	tagTypes: ['Filter', 'SavedSearch'],
	endpoints: (builder) => ({
		// Get filter metadata
		getFilterMetadata: builder.query<FilterMetadata, FilterSearchRequestParams>({
			query: ({ accountId, accountTypeCode, sourceId, filters }) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				const paramString = params.size > 0 ? `&${params.toString()}` : '';
				return `metadata?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}${paramString}`;
			},
			providesTags: ['Filter'],
		}),
		getAccountDetailsFields: builder.query<FilterField[], FilterSearchRequestParams>({
			query: ({ accountId, accountTypeCode, sourceId, filters }) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				const paramString = params.size > 0 ? `&${params.toString()}` : '';
				return `metadata/account-details?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}${paramString}`;
			},
			providesTags: ['Filter'],
		}),
		getAssetDetailsFields: builder.query<FilterField[], FilterSearchRequestParams>({
			query: ({ accountId, accountTypeCode, sourceId, filters }) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				const paramString = params.size > 0 ? `&${params.toString()}` : '';
				return `metadata/asset-details?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}${paramString}`;
			},
			providesTags: ['Filter'],
		}),
		getAssetLocationFields: builder.query<FilterField[], FilterSearchRequestParams>({
			query: ({ accountId, accountTypeCode, sourceId, filters }) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				const paramString = params.size > 0 ? `&${params.toString()}` : '';
				return `metadata/asset-location?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}${paramString}`;
			},
			providesTags: ['Filter'],
		}),
		getBillingDetailsFields: builder.query<FilterField[], FilterSearchRequestParams>({
			query: ({ accountId, accountTypeCode, sourceId, filters }) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				const paramString = params.size > 0 ? `&${params.toString()}` : '';
				return `metadata/billing-details?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}${paramString}`;
			},
			providesTags: ['Filter'],
		}),
		getClientAttributesFields: builder.query<FilterField[], FilterSearchRequestParams>({
			query: ({ accountId, accountTypeCode, sourceId, filters }) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				const paramString = params.size > 0 ? `&${params.toString()}` : '';
				return `metadata/client-attributes?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}${paramString}`;
			},
			providesTags: ['Filter'],
		}),
		getContractDetailsFields: builder.query<FilterField[], FilterSearchRequestParams>({
			query: ({ accountId, accountTypeCode, sourceId, filters }) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				const paramString = params.size > 0 ? `&${params.toString()}` : '';
				return `metadata/contract-details?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}${paramString}`;
			},
			providesTags: ['Filter'],
		}),
		getEndOfLeaseDetailsFields: builder.query<FilterField[], FilterSearchRequestParams>({
			query: ({ accountId, accountTypeCode, sourceId, filters }) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				const paramString = params.size > 0 ? `&${params.toString()}` : '';
				return `metadata/end-of-lease-details?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}${paramString}`;
			},
			providesTags: ['Filter'],
		}),
		getSupplierDetailsFields: builder.query<FilterField[], FilterSearchRequestParams>({
			query: ({ accountId, accountTypeCode, sourceId, filters }) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				const paramString = params.size > 0 ? `&${params.toString()}` : '';
				return `metadata/supplier-details?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}${paramString}`;
			},
			providesTags: ['Filter'],
		}),
		getInvoiceFields: builder.query<FilterField[], FilterSearchRequestParams>({
			query: ({ accountId, accountTypeCode, sourceId, filters }) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				const paramString = params.size > 0 ? `&${params.toString()}` : '';
				return `metadata/invoice-details?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}${paramString}`;
			},
			providesTags: ['Filter'],
		}),
		// Search endpoints for asset numbers and serial numbers
		searchAssetNumbers: builder.query<
			string[],
			FilterSearchRequestParams & { searchString: string }
		>({
			query: ({ accountId, accountTypeCode, sourceId, filters, searchString }) => {
				const params = new URLSearchParams();
				params.append('accountId', accountId.toString());
				params.append('accountTypeCode', accountTypeCode);
				params.append('sourceId', sourceId.toString());
				params.append('searchString', searchString);
				if (filters) handleFilterParams(filters, params);
				return `metadata/asset-number-search?${params.toString()}`;
			},
			providesTags: ['Filter'],
		}),
		searchSerialNumbers: builder.query<
			string[],
			FilterSearchRequestParams & { searchString: string }
		>({
			query: ({ accountId, accountTypeCode, sourceId, filters, searchString }) => {
				const params = new URLSearchParams();
				params.append('accountId', accountId.toString());
				params.append('accountTypeCode', accountTypeCode);
				params.append('sourceId', sourceId.toString());
				params.append('searchString', searchString);
				if (filters) handleFilterParams(filters, params);
				return `metadata/serial-number-search?${params.toString()}`;
			},
			providesTags: ['Filter'],
		}),
	}),
});

export const {
	useGetFilterMetadataQuery,
	useGetAccountDetailsFieldsQuery,
	useGetAssetDetailsFieldsQuery,
	useGetAssetLocationFieldsQuery,
	useGetBillingDetailsFieldsQuery,
	useGetClientAttributesFieldsQuery,
	useGetContractDetailsFieldsQuery,
	useGetEndOfLeaseDetailsFieldsQuery,
	useGetSupplierDetailsFieldsQuery,
	useGetInvoiceFieldsQuery,
	useSearchAssetNumbersQuery,
	useSearchSerialNumbersQuery,
} = filtersApi;
